<template>
  <div class="mx-auto flex w-full max-w-2xl flex-col pb-8 lg:pb-0">
    <div
      ref="rootEl"
      class="group rounded-large select-non relative box-content flex touch-none items-center pt-16 outline-none"
      :class="[
        trackHeight,
        disabled && 'pointer-events-none opacity-40',
        !disabled && !dragging && 'cursor-pointer',
        !disabled && dragging && 'cursor-grabbing',
      ]"
      role="slider"
      :tabindex="disabled ? -1 : 0"
      :aria-valuemin="min"
      :aria-valuemax="max"
      :aria-valuenow="isUnset ? undefined : (displayValue as number)"
      :aria-valuetext="isUnset ? 'No value set' : String(displayValue)"
      :aria-disabled="disabled"
      :aria-keyshortcuts="ariaKeyshortcuts"
      @pointerdown="onPointerdown"
      @pointermove="onPointermove"
      @pointerup="endDrag"
      @pointercancel="endDrag"
      @pointerenter="hovering = true"
      @pointerleave="hovering = false"
      @focus="focused = true"
      @blur="focused = false"
    >
      <div
        ref="railEl"
        class="relative w-full"
        :class="[trackHeight]"
        :style="{ marginLeft: X_MARGIN + 'px', marginRight: X_MARGIN + 'px' }"
      >
        <template v-if="!isCentered">
          <!-- inactive track -->
          <div
            class="bg-secondary-container ease-fast-effect absolute top-0 h-full transition-[width] duration-100 motion-reduce:transition-none"
            :style="{ width: emptyTrackWidth, right: '-' + X_MARGIN + 'px' }"
            :class="[emptyTrackRadius]"
          ></div>

          <!-- active (filled) track -->
          <div
            class="bg-primary rounded-r-extra-small ease-fast-effect absolute top-0 h-full transition-[width] duration-100 motion-reduce:transition-none"
            :style="{ width: filledTrackWidth, left: '-' + X_MARGIN + 'px' }"
            :class="[filledTrackRadius]"
          ></div>
        </template>
        <template v-else>
          <div
            class="bg-secondary-container rounded-r-extra-small ease-fast-effect absolute top-0 h-full transition-[width] duration-100 motion-reduce:transition-none"
            :style="{
              width: centeredEmptyTrackLeftWidth,
              left: '-' + X_MARGIN + 'px',
            }"
            :class="[filledTrackRadius]"
          ></div>
          <div
            class="bg-secondary-container rounded-l-extra-small ease-fast-effect absolute top-0 h-full transition-[width] duration-100 motion-reduce:transition-none"
            :style="{
              width: centeredEmptyTrackRightWidth,
              right: '-' + X_MARGIN + 'px',
            }"
            :class="[roundedRight]"
          ></div>
          <div
            class="bg-primary rounded-extra-small ease-fast-effect absolute top-0 h-full transition-[width] duration-100 motion-reduce:transition-none"
            :style="{
              width: isLeft ? centeredFilledTrackWidth : '0px',
              right: 'calc(50% + ' + THUMB_GAP / 2 + 'px)',
            }"
          ></div>
          <div
            class="bg-primary rounded-extra-small ease-fast-effect absolute top-0 h-full transition-[width] duration-100 motion-reduce:transition-none"
            :style="{
              width: !isLeft ? centeredFilledTrackWidth : '0px',
              left: 'calc(50% + ' + THUMB_GAP / 2 + 'px)',
            }"
          ></div>
        </template>

        <!-- stops -->
        <span
          v-for="t in stops"
          :key="t"
          class="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full transition-all duration-200 motion-reduce:transition-none"
          :class="[
            t <= (displayValue as number) && !isCentered && 'bg-on-primary',
            (t > (displayValue as number) || isUnset) && 'bg-on-secondary-container',
            isUnset && 'border-on-secondary-container h-2.5 w-2.5 border-2 bg-transparent',
            !isUnset && 'h-1 w-1',
            isCentered &&
              displayValue !== undefined &&
              displayValue < t &&
              t < centerValue &&
              'bg-on-primary!',
            isCentered &&
              displayValue !== undefined &&
              displayValue > t &&
              t > centerValue &&
              'bg-on-primary!',
            isCentered && 'bg-on-secondary-container',
            isCentered && t === centerValue && !isUnset && 'hidden',
          ]"
          :style="{ left: calculatePercentage(t) + '%' }"
        ></span>

        <!-- thumb -->
        <div
          v-if="!isUnset"
          class="ease-fast-effect bg-surface absolute top-1/2 flex -translate-x-1/2 -translate-y-1/2 justify-center transition-[left] duration-100 motion-reduce:transition-none"
          :style="{
            left: thumbPercentage + '%',
            paddingLeft: THUMB_GAP + 'px',
            paddingRight: THUMB_GAP + 'px',
          }"
        >
          <span
            class="bg-primary rounded-small relative block"
            :class="[thumbHeight, !dragging && 'w-1.5', dragging && 'w-0.5']"
          ></span>
        </div>

        <!-- label -->
        <div
          v-if="!isUnset"
          ref="thumbEl"
          class="pointer-events-none absolute top-1/2 z-30 h-full w-full -translate-y-1/2 sm:-translate-x-1/2"
          :style="{ left: centerLabel ? 0 : thumbPercentage + '%' }"
        >
          <div
            ref="labelEl"
            :style="centerLabel ? undefined : floatingStyles"
            class="pointer-events-none absolute z-10 -mt-16 flex w-full justify-center sm:mt-0 sm:block sm:w-auto"
          >
            <Transition
              enter-from-class="opacity-0 scale-0"
              enter-to-class="scale-100 opacity-100"
              enter-active-class="transition-all duration-200 delay-100 ease-slow-spatial motion-reduce:transition-none"
              leave-active-class="transition-none duration-0 absolute opacity-0"
            >
              <div
                :key="selectedScaleItem.value"
                class="bg-inverse-surface text-inverse-on-surface text-body-large origin-bottom rounded-full px-5 py-4 whitespace-nowrap select-none"
              >
                {{ displayLabel }}
              </div>
            </Transition>
          </div>
        </div>
      </div>
    </div>

    <div
      class="text-body-small sm:text-title-medium text-on-surface-variant relative mt-4 flex w-full flex-row justify-between px-2"
    >
      <span
        class="shrink-0 text-left md:-translate-x-1/2 md:text-center"
        :style="{ width: 100 / scale.items.length + '%' }"
        >{{ scale.items[0].label }}</span
      >
      <span
        v-if="scale.isOptimumPoint && middleLabel"
        class="absolute left-1/2 -translate-x-1/2 text-center"
        >{{ middleLabel }}</span
      >
      <span
        class="text-right md:translate-x-1/2 md:text-center"
        :style="{ width: 100 / scale.items.length + '%' }"
        >{{ scale.items[scale.items.length - 1].label }}</span
      >
    </div>
    <div class="mt-3 flex h-8 flex-row items-center justify-center">
      <span
        v-if="isUnset"
        class="text-body-medium text-on-surface-variant bg-surface-container-high rounded-small inline-block px-2.5 py-1"
        >Keine Antwort gewählt</span
      >
      <BaseTextButton
        v-if="!isUnset && value !== undefined"
        @click="reset"
        color="outlined"
        size="XS"
        icon="undo"
        >Antwort zurücksetzen</BaseTextButton
      >
    </div>

    <div class="flex flex-row"></div>
  </div>
</template>

<script setup lang="ts">
  import BaseTextButton from '@/core/components/base/button/text-button/BaseTextButton.vue'
  import { useBreakpoint } from '@/core/composables/use-break-points'
  import type { SliderScale } from '@/core/models/scale'
  import { offset, shift, useFloating } from '@floating-ui/vue'
  import { ref, computed, nextTick, useTemplateRef, watch } from 'vue'

  type EmptyPosition = 'start' | 'center' | 'end'

  interface Props {
    scale: SliderScale
    showStops?: boolean
    emptyPosition?: EmptyPosition
    disabled?: boolean
    size?: 'XS' | 'S' | 'M' | 'L' | 'XL'
  }

  const {
    scale,
    showStops = true,
    emptyPosition = 'center',
    disabled = false,
    size = 'S',
  } = defineProps<Props>()

  const value = defineModel<number | undefined>({ required: true })

  const isCentered = computed(() => {
    return scale.isOptimumPoint
  })

  const denormalizedValue = computed(() => {
    return value.value !== undefined
      ? scale.items.findIndex((item) => item.value === value.value)
      : undefined
  })

  const emit = defineEmits<{
    change: [value: number]
  }>()

  const breakpoint = useBreakpoint()
  const centerLabel = breakpoint.breakpoints.smaller('sm')

  const rootEl = ref<HTMLElement | null>(null)
  const railEl = ref<HTMLElement | null>(null)
  const thumbEl = useTemplateRef<HTMLElement>('thumbEl')
  const labelEl = useTemplateRef<HTMLElement>('labelEl')
  const dragging = ref(false)
  const dragValue = ref<number | null>(null)
  const hovering = ref(false)
  const focused = ref(false)

  const step = computed(() => 1)
  const min = computed(() => 0)
  const max = computed(() => scale.items.length - 1)
  const centerValue = computed(() => Math.floor(scale.items.length / 2))

  const displayValue = computed(() =>
    dragging.value && dragValue.value !== null ? snap(dragValue.value) : denormalizedValue.value,
  )

  const isUnset = computed(
    () => (value.value === null || value.value === undefined) && dragValue.value === null,
  )

  const emptyPct = computed(() =>
    emptyPosition === 'start' ? 0 : emptyPosition === 'end' ? 100 : 50,
  )

  const selectedScaleItem = computed(() => scale.items[displayValue.value!])

  const displayLabel = computed(() => {
    return selectedScaleItem.value?.label ?? '—'
  })

  const calculatePercentage = (v: number): number => (v / (scale.items.length - 1)) * 100

  const thumbPercentage = computed(() =>
    isUnset.value ? emptyPct.value : calculatePercentage(displayValue.value!),
  )

  const X_MARGIN = 6
  const THUMB_GAP = 6

  const handleWidth = computed(() => {
    return dragging.value ? 2 : 4
  })

  const { floatingStyles, update } = useFloating(thumbEl, labelEl, {
    transform: false,
    placement: 'top',
    middleware: [offset(8), shift({ padding: X_MARGIN })],
  })

  watch([value, displayLabel], async () => {
    await nextTick()
    update()
  })

  const filledTrackWidth = computed(() => {
    if (isUnset.value || displayValue.value === 0) {
      return 0
    }
    return `calc(${calculatePercentage(displayValue.value!)}% - ${THUMB_GAP}px - ${handleWidth.value / 2}px + ${X_MARGIN}px)`
  })

  const emptyTrackWidth = computed(() => {
    if (isUnset.value) {
      return `calc(100% + ${2 * X_MARGIN}px)`
    }
    if (displayValue.value === max.value) {
      return '0px'
    }
    return `calc(${calculatePercentage(max.value - displayValue.value!)}% - ${THUMB_GAP}px - ${handleWidth.value / 2}px + ${X_MARGIN}px)`
  })

  const centeredEmptyTrackLeftWidth = computed(() => {
    if (isUnset.value) {
      return `calc(50% + ${X_MARGIN}px + 2px)`
    }
    if (displayValue.value === 0) {
      return `0px`
    }
    if (isLeft.value) {
      return `calc(${calculatePercentage(displayValue.value!)}% - ${THUMB_GAP}px - ${handleWidth.value / 2}px + ${X_MARGIN}px)`
    }
    return `calc(50% + ${X_MARGIN}px - ${THUMB_GAP / 2}px)`
  })

  const centeredEmptyTrackRightWidth = computed(() => {
    if (isUnset.value) {
      return `calc(50% + ${X_MARGIN}px + 2px)`
    }
    if (displayValue.value === max.value) {
      return `0px`
    }
    if (!isLeft.value) {
      return `calc(${calculatePercentage(max.value - displayValue.value!)}% - ${THUMB_GAP}px - ${handleWidth.value / 2}px + ${X_MARGIN}px)`
    }
    return `calc(50% + ${X_MARGIN}px - ${THUMB_GAP / 2}px)`
  })

  const centeredFilledTrackWidth = computed(() => {
    if (isUnset.value) {
      return `0px`
    }
    return `calc(${calculatePercentage(Math.abs(displayValue.value! - centerValue.value))}% - ${1.5 * THUMB_GAP}px - ${handleWidth.value / 2}px)`
  })

  const isLeft = computed(() => {
    return displayValue.value !== undefined && displayValue.value <= centerValue.value
  })

  const stops = computed<number[]>(() => {
    if (!showStops || step.value <= 0) return []
    const arr: number[] = []
    for (let v = min.value; v <= max.value; v += step.value) arr.push(v)
    return arr
  })

  const snap = (raw: number): number => {
    const s = Math.round((raw - min.value) / step.value) * step.value + min.value
    return Math.min(max.value, Math.max(min.value, s))
  }

  const previewValue = (v: number): void => {
    if (disabled) return
    dragValue.value = snap(v)
    nextTick(() => update())
  }

  const commitValue = (v: number): void => {
    if (disabled) return
    const nv = scale.items[snap(v)]
    if (nv && nv.value !== value.value) {
      value.value = nv.value
      emit('change', nv.value)
    }
  }

  const fromClientX = (x: number): number => {
    const el = railEl.value
    if (!el) return min.value
    const r = el.getBoundingClientRect()
    const ratio = Math.min(1, Math.max(0, (x - r.left) / r.width))
    return min.value + ratio * (max.value - min.value)
  }

  const onPointerdown = (e: PointerEvent): void => {
    if (disabled) return
    dragging.value = true
    rootEl.value?.setPointerCapture(e.pointerId)
    rootEl.value?.focus()
    previewValue(fromClientX(e.clientX))
  }

  const onPointermove = (e: PointerEvent): void => {
    if (!dragging.value) return
    previewValue(fromClientX(e.clientX))
  }

  const endDrag = (e: PointerEvent): void => {
    if (!dragging.value) return
    if (dragValue.value !== null) {
      commitValue(dragValue.value)
    }
    dragValue.value = null
    dragging.value = false
    if (rootEl.value?.hasPointerCapture(e.pointerId)) {
      rootEl.value.releasePointerCapture(e.pointerId)
    }
  }

  const reset = () => {
    value.value = undefined
  }

  const middleLabel = computed(() => {
    if (!scale.isOptimumPoint) return undefined
    if (scale.items.length % 2 === 0) return undefined
    return scale.items[centerValue.value].label
  })

  const trackHeight = computed(() => {
    switch (size) {
      case 'XS':
        return 'h-4'
      case 'S':
        return 'h-6'
      case 'M':
        return 'h-10'
      case 'L':
        return 'h-14'
      case 'XL':
        return 'h-24'
    }
  })

  const filledTrackRadius = computed(() => {
    switch (size) {
      case 'XS':
        return 'rounded-l-small'
      case 'S':
        return 'rounded-l-small'
      case 'M':
        return 'rounded-l-medium'
      case 'L':
        return 'rounded-l-large'
      case 'XL':
        return 'rounded-l-extra-large'
    }
  })

  const roundedRight = computed(() => {
    switch (size) {
      case 'XS':
        return 'rounded-r-small'
      case 'S':
        return 'rounded-r-small'
      case 'M':
        return 'rounded-r-medium'
      case 'L':
        return 'rounded-r-large'
      case 'XL':
        return 'rounded-r-extra-large'
    }
  })

  const emptyTrackRadius = computed(() => {
    let cssClass = ''
    switch (size) {
      case 'XS':
        cssClass = 'rounded-r-small'
        break
      case 'S':
        cssClass = 'rounded-r-small'
        break
      case 'M':
        cssClass = 'rounded-r-medium'
        break
      case 'L':
        cssClass = 'rounded-r-large'
        break
      case 'XL':
        cssClass = 'rounded-r-extra-large'
        break
    }

    if (isUnset.value) {
      switch (size) {
        case 'XS':
          cssClass += ' rounded-l-small'
          break
        case 'S':
          cssClass += ' rounded-l-small'
          break
        case 'M':
          cssClass += ' rounded-l-medium'
          break
        case 'L':
          cssClass += ' rounded-l-large'
          break
        case 'XL':
          cssClass += ' rounded-l-extra-large'
          break
      }
    } else {
      cssClass += ' rounded-l-small'
    }
    return cssClass
  })

  const thumbHeight = computed(() => {
    switch (size) {
      case 'XS':
        return 'h-11'
      case 'S':
        return 'h-11'
      case 'M':
        return 'h-13'
      case 'L':
        return 'h-16'
      case 'XL':
        return 'h-[108px]'
    }
  })

  const ariaKeyshortcuts = computed(() => {
    return scale.items.map((_, index) => index + 1).join(' ')
  })
</script>
